/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   A specially-typed version of compoose for use with HOCs.
 */

import { NoNevers } from '@zcode/utility-types'
import { ComponentType } from 'react'
import {
  ConnectedComponent,
  GetProps,
  InferableComponentEnhancerWithProps,
  Matching,
  Shared,
} from 'react-redux'
import { compose } from 'redux'

export interface HocCompose {
  <
    AInjectedProps,
    BInjectedProps,
    ANeedsProps,
    BNeedsProps,
    Component extends ComponentType<
      Matching<AInjectedProps & BInjectedProps, GetProps<Component>>
    >,
  >(
    f: InferableComponentEnhancerWithProps<AInjectedProps, ANeedsProps>,
    g: InferableComponentEnhancerWithProps<BInjectedProps, BNeedsProps>,
    component: Component
  ): ConnectedComponent<
    Component,
    Omit<
      GetProps<Component>,
      keyof Shared<AInjectedProps & BInjectedProps, GetProps<Component>>
    > &
      (NoNevers<ANeedsProps> extends never ? {} : NoNevers<ANeedsProps>) &
      (Omit<BNeedsProps, keyof AInjectedProps> extends never
        ? {}
        : NoNevers<Omit<BNeedsProps, keyof AInjectedProps>>)
  >

  <
    AInjectedProps,
    BInjectedProps,
    CInjectedProps,
    ANeedsProps,
    BNeedsProps,
    CNeedsProps,
    Component extends ComponentType<
      Matching<
        AInjectedProps & BInjectedProps & CInjectedProps,
        GetProps<Component>
      >
    >,
  >(
    f: InferableComponentEnhancerWithProps<AInjectedProps, ANeedsProps>,
    g: InferableComponentEnhancerWithProps<BInjectedProps, BNeedsProps>,
    h: InferableComponentEnhancerWithProps<CInjectedProps, CNeedsProps>,
    component: Component
  ): ConnectedComponent<
    Component,
    Omit<
      GetProps<Component>,
      keyof Shared<
        AInjectedProps & BInjectedProps & CInjectedProps,
        GetProps<Component>
      >
    > &
      (NoNevers<ANeedsProps> extends never ? {} : NoNevers<ANeedsProps>) &
      (Omit<BNeedsProps, keyof AInjectedProps> extends never
        ? {}
        : NoNevers<Omit<BNeedsProps, keyof AInjectedProps>>) &
      (Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)> extends never
        ? {}
        : NoNevers<Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)>>)
  >

  <
    AInjectedProps,
    BInjectedProps,
    CInjectedProps,
    DInjectedProps,
    ANeedsProps,
    BNeedsProps,
    CNeedsProps,
    DNeedsProps,
    Component extends ComponentType<
      Matching<
        AInjectedProps & BInjectedProps & CInjectedProps & DInjectedProps,
        GetProps<Component>
      >
    >,
  >(
    f: InferableComponentEnhancerWithProps<AInjectedProps, ANeedsProps>,
    g: InferableComponentEnhancerWithProps<BInjectedProps, BNeedsProps>,
    h: InferableComponentEnhancerWithProps<CInjectedProps, CNeedsProps>,
    i: InferableComponentEnhancerWithProps<DInjectedProps, DNeedsProps>,
    component: Component
  ): ConnectedComponent<
    Component,
    Omit<
      GetProps<Component>,
      keyof Shared<
        AInjectedProps & BInjectedProps & CInjectedProps & DInjectedProps,
        GetProps<Component>
      >
    > &
      (NoNevers<ANeedsProps> extends never ? {} : NoNevers<ANeedsProps>) &
      (Omit<BNeedsProps, keyof AInjectedProps> extends never
        ? {}
        : NoNevers<Omit<BNeedsProps, keyof AInjectedProps>>) &
      (Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)> extends never
        ? {}
        : NoNevers<
            Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)>
          >) &
      (Omit<
        DNeedsProps,
        keyof (AInjectedProps & BInjectedProps & CInjectedProps)
      > extends never
        ? {}
        : NoNevers<
            Omit<
              DNeedsProps,
              keyof (AInjectedProps & BInjectedProps & CInjectedProps)
            >
          >)
  >

  <
    AInjectedProps,
    BInjectedProps,
    CInjectedProps,
    DInjectedProps,
    EInjectedProps,
    ANeedsProps,
    BNeedsProps,
    CNeedsProps,
    DNeedsProps,
    ENeedsProps,
    Component extends ComponentType<
      Matching<
        AInjectedProps &
          BInjectedProps &
          CInjectedProps &
          DInjectedProps &
          EInjectedProps,
        GetProps<Component>
      >
    >,
  >(
    f: InferableComponentEnhancerWithProps<AInjectedProps, ANeedsProps>,
    g: InferableComponentEnhancerWithProps<BInjectedProps, BNeedsProps>,
    h: InferableComponentEnhancerWithProps<CInjectedProps, CNeedsProps>,
    i: InferableComponentEnhancerWithProps<DInjectedProps, DNeedsProps>,
    j: InferableComponentEnhancerWithProps<EInjectedProps, ENeedsProps>,
    component: Component
  ): ConnectedComponent<
    Component,
    Omit<
      GetProps<Component>,
      keyof Shared<
        AInjectedProps &
          BInjectedProps &
          CInjectedProps &
          DInjectedProps &
          EInjectedProps,
        GetProps<Component>
      >
    > &
      (NoNevers<ANeedsProps> extends never ? {} : NoNevers<ANeedsProps>) &
      (Omit<BNeedsProps, keyof AInjectedProps> extends never
        ? {}
        : NoNevers<Omit<BNeedsProps, keyof AInjectedProps>>) &
      (Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)> extends never
        ? {}
        : NoNevers<
            Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)>
          >) &
      (Omit<
        DNeedsProps,
        keyof (AInjectedProps & BInjectedProps & CInjectedProps)
      > extends never
        ? {}
        : NoNevers<
            Omit<
              DNeedsProps,
              keyof (AInjectedProps & BInjectedProps & CInjectedProps)
            >
          >) &
      (Omit<
        ENeedsProps,
        keyof (AInjectedProps &
          BInjectedProps &
          CInjectedProps &
          DInjectedProps)
      > extends never
        ? {}
        : NoNevers<
            Omit<
              ENeedsProps,
              keyof (AInjectedProps &
                BInjectedProps &
                CInjectedProps &
                DInjectedProps)
            >
          >)
  >

  <
    AInjectedProps,
    BInjectedProps,
    CInjectedProps,
    DInjectedProps,
    EInjectedProps,
    FInjectedProps,
    ANeedsProps,
    BNeedsProps,
    CNeedsProps,
    DNeedsProps,
    ENeedsProps,
    FNeedsProps,
    Component extends ComponentType<
      Matching<
        AInjectedProps &
          BInjectedProps &
          CInjectedProps &
          DInjectedProps &
          EInjectedProps &
          FInjectedProps,
        GetProps<Component>
      >
    >,
  >(
    f: InferableComponentEnhancerWithProps<AInjectedProps, ANeedsProps>,
    g: InferableComponentEnhancerWithProps<BInjectedProps, BNeedsProps>,
    h: InferableComponentEnhancerWithProps<CInjectedProps, CNeedsProps>,
    i: InferableComponentEnhancerWithProps<DInjectedProps, DNeedsProps>,
    j: InferableComponentEnhancerWithProps<EInjectedProps, ENeedsProps>,
    k: InferableComponentEnhancerWithProps<FInjectedProps, FNeedsProps>,
    component: Component
  ): ConnectedComponent<
    Component,
    Omit<
      GetProps<Component>,
      keyof Shared<
        AInjectedProps &
          BInjectedProps &
          CInjectedProps &
          DInjectedProps &
          EInjectedProps &
          FInjectedProps,
        GetProps<Component>
      >
    > &
      (NoNevers<ANeedsProps> extends never ? {} : NoNevers<ANeedsProps>) &
      (Omit<BNeedsProps, keyof AInjectedProps> extends never
        ? {}
        : NoNevers<Omit<BNeedsProps, keyof AInjectedProps>>) &
      (Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)> extends never
        ? {}
        : NoNevers<
            Omit<CNeedsProps, keyof (AInjectedProps & BInjectedProps)>
          >) &
      (Omit<
        DNeedsProps,
        keyof (AInjectedProps & BInjectedProps & CInjectedProps)
      > extends never
        ? {}
        : NoNevers<
            Omit<
              DNeedsProps,
              keyof (AInjectedProps & BInjectedProps & CInjectedProps)
            >
          >) &
      (Omit<
        ENeedsProps,
        keyof (AInjectedProps &
          BInjectedProps &
          CInjectedProps &
          DInjectedProps)
      > extends never
        ? {}
        : NoNevers<
            Omit<
              ENeedsProps,
              keyof (AInjectedProps &
                BInjectedProps &
                CInjectedProps &
                DInjectedProps)
            >
          >) &
      (Omit<
        FNeedsProps,
        keyof (AInjectedProps &
          BInjectedProps &
          CInjectedProps &
          DInjectedProps &
          EInjectedProps)
      > extends never
        ? {}
        : NoNevers<
            Omit<
              FNeedsProps,
              keyof (AInjectedProps &
                BInjectedProps &
                CInjectedProps &
                DInjectedProps &
                EInjectedProps)
            >
          >)
  >
}

export const hocCompose: HocCompose = (...args: any[]) => {
  const component = args.pop()
  return (compose as any)(...args)(component)
}
